import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@guestpilot/ui-kit";
import { ApiProblemError } from "./api/client";
import { useT } from "./i18n";

export interface FieldErrors {
  [field: string]: string;
}

/** Field errors of a validation problem, keyed by field name (shown under the inputs, linked with aria-describedby). */
export function fieldErrorsOf(err: unknown): FieldErrors {
  if (!(err instanceof ApiProblemError)) return {};
  return Object.fromEntries(
    (err.problem.errors ?? []).flatMap((e) => (e.field && e.message ? [[e.field, e.message]] : [])),
  );
}

/**
 * One place that turns a problem into words (standards/frontend): a localised message by `code`, with the server detail for the
 * codes that carry a useful one; account-state codes also refresh `/me`, so banners and the route guard follow at once.
 */
export function useHandleApiError() {
  const t = useT();
  const toast = useToast();
  const qc = useQueryClient();
  return useCallback(
    (err: unknown) => {
      if (!(err instanceof ApiProblemError)) {
        toast.error(t("errors:internal_error"));
        return;
      }
      const code = err.problem.code;
      if (code === "tenant_read_only" || code === "tenant_blocked" || code === "tenant_archived")
        void qc.invalidateQueries({ queryKey: ["/api/v1/me"] });
      const known = t(`errors:${code}`);
      const message = known === `errors:${code}` ? (err.problem.detail ?? t("errors:internal_error")) : known;
      if (code === "validation_failed" && err.problem.errors?.length) return; // shown next to the fields
      toast.error(message);
    },
    [t, toast, qc],
  );
}
